import { referenceDisplay } from '@seen/core/display'
import type { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { isOfType } from '@seen/core/utils'

import { Button, cn, DotSeparator, TypographyDetail } from '@seen/ui'
import { type JSX, useState } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { DocumentReferenceSheet } from '#src/components/document-reference/DocumentReferenceSheet.tsx'
import { Logo } from '#src/components/index.ts'
import { useCreatedBy } from '#src/hooks/useCreatedBy.ts'
import { useWarningReporter } from '#src/hooks/useWarningReporter.ts'

interface GroupedDocumentReferencesItemProps {
  documentReferences: ExtendedDocumentReference[]
}

export function GroupedDocumentReferencesItem({
  documentReferences,
}: GroupedDocumentReferencesItemProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedDocument, setSelectedDocument] =
    useState<ExtendedDocumentReference | null>(null)

  // Use the first document for the main entry display
  const firstDoc = documentReferences[0]
  const count = documentReferences.length

  useWarningReporter(
    !firstDoc.author && `${firstDoc.reference.reference} missing author`
  )

  const { data: createdBy, isLoading: createdByIsLoading } = useCreatedBy(
    firstDoc.reference
  )

  const actor = firstDoc.author ?? createdBy ?? 'Unknown'
  const actorIsLoading = !firstDoc.author && createdByIsLoading

  return (
    <>
      <div
        className={cn(
          'flex justify-between gap-2 py-1',
          'group overflow-hidden'
        )}
      >
        <div className="flex w-full gap-2">
          <div
            className={cn([
              'ml-2.5',
              '-my-1.5 z-10 bg-background py-1.5',
              'group-last:mb-[-100rem] group-last:pb-[100rem]',
            ])}
          >
            {actorIsLoading && <Avatar profile={undefined} size="icon" />}
            {!actorIsLoading && (
              <>
                {actor !== 'Unknown' && <Avatar profile={actor} size="icon" />}
                {actor === 'Unknown' && (
                  <div className="flex size-5 items-center justify-center rounded-full bg-muted">
                    <Logo size={12} />
                  </div>
                )}
              </>
            )}
          </div>
          <div className={cn('flex w-full min-w-0 flex-col', 'pt-0.5')}>
            <TypographyDetail>
              {actorIsLoading && <HoverPractitioner practitioner={undefined} />}
              {!actorIsLoading && (
                <>
                  {actor !== 'Unknown' && isOfType(actor, 'Practitioner') && (
                    <HoverPractitioner practitioner={actor} />
                  )}
                  {actor !== 'Unknown' && isOfType(actor, 'Organization') && (
                    <span>{referenceDisplay(actor)}</span>
                  )}
                  {actor === 'Unknown' && 'System'}
                </>
              )}
              <span className="font-normal text-foreground-secondary">
                {' '}
                uploaded{' '}
                <Button
                  variant="link"
                  size="xs"
                  onClick={() => {
                    setIsExpanded(!isExpanded)
                  }}
                  className="inline-flex h-auto items-center gap-0.5 p-0"
                >
                  {count} documents
                </Button>
                <DotSeparator />
                {firstDoc.date && (
                  <HoverDate date={firstDoc.date} format="datetime" />
                )}
                {!firstDoc.date && 'Unknown time'}
              </span>
            </TypographyDetail>
            {isExpanded && (
              <div className="mt-1.5 flex flex-col gap-0.5">
                {documentReferences.map((doc) => (
                  <TypographyDetail
                    key={doc.reference.reference}
                    className="flex items-center gap-1 text-foreground-secondary"
                  >
                    <button
                      type="button"
                      className="cursor-pointer text-left font-medium focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden"
                      onClick={() => {
                        setSelectedDocument(doc)
                      }}
                    >
                      {doc.displayTitle}
                    </button>
                    <DotSeparator />
                    {doc.date && (
                      <HoverDate date={doc.date} format="datetime" />
                    )}
                  </TypographyDetail>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <DocumentReferenceSheet
        documentReference={selectedDocument}
        open={!!selectedDocument}
        setOpen={(open) => {
          if (!open) {
            setSelectedDocument(null)
          }
        }}
        hideDelete
        hideParticipant
      />
    </>
  )
}
